import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Button } from 'common-src/components/base';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { getPatient } from 'common-src/models/Patient';
import { getPrimaryPatientPhoneNumber } from 'common-src/models/PatientPhoneNumber';

import { InfoItem } from 'src/components/elements';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useOpenWindow from 'src/hooks/useOpenWindow';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './VideoCallInformationPopup.module.scss';

const VideoCallInformationPopup = ({ patientId, meetingId, patientUrl, clientUrl, onClose }) => {
  const [isSmsLoading, setIsSmsLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  const patient = useCustomSelector((state) => getPatient(state, patientId));
  const primaryPhoneNumber = useCustomSelector((state) =>
    getPrimaryPatientPhoneNumber(state, patientId),
  );

  const companyName = patient.isMindspanPatient ? 'Mindspan' : 'Perry Health';
  const patientMessage = `Please join your video call with ${companyName} by clicking on this link: ${patientUrl}`;

  const { renderButtons } = usePopup();
  const { showAlert, AlertType } = useAlert();
  const { openWindow } = useOpenWindow();

  const copyLinkHandler = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      showAlert(AlertType.Success, '', 'Link copied to clipboard.');
    });
  };

  const openInBrowserHandler = () => {
    openWindow({
      url: clientUrl,
      target: '_blank',
      features: 'width=800,height=600,menubar=no,toolbar=no,location=no,status=no',
    });
  };

  const sendSmsToMemberHandler = () => {
    setIsSmsLoading(true);
    apiRequest({
      endpoint: 'awscChatMessage',
      method: HttpMethods.Post,
      body: {
        patientId,
        outboundPhoneNumber: primaryPhoneNumber?.phoneNumber,
        message: patientMessage,
      },
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const errorBody = await res.json();
          throw new Error(
            errorBody?.user_message ?? errorBody?.detail ?? 'Error while sending SMS.',
          );
        }
        return res.json();
      })
      .then(() => showAlert(AlertType.Success, '', 'Successfully sent link to member'))
      .catch((error) => {
        showAlert(AlertType.Error, '', error.message);
      })
      .finally(() => setIsSmsLoading(false));
  };

  const sendEmailToMemberHandler = () => {
    setIsEmailLoading(true);
    apiRequest({
      endpoint: `videoCalls/${meetingId}/email`,
      method: HttpMethods.Post,
      body: {
        url: patientUrl,
      },
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const errorBody = await res.json();
          throw new Error(
            errorBody?.user_message ?? errorBody?.detail ?? 'Error while sending email.',
          );
        }
        return res.json();
      })
      .then(() => showAlert(AlertType.Success, '', 'Successfully sent email'))
      .catch((error) => {
        showAlert(AlertType.Error, '', error.message);
      })
      .finally(() => setIsEmailLoading(false));
  };

  return (
    <BasePopup
      id="video-call-information"
      open
      onClose={onClose}
      title="Video Call Information"
      customStyle={{ minWidth: '40vw' }}
    >
      <div className={styles.content}>
        <div className={[styles.row, 'vertically-centered', 'gap-16', 'm-b-16'].join(' ')}>
          <InfoItem
            classNames={['flex-1']}
            title="Link to join the video call"
            content={clientUrl}
            textId="client-video-call-link"
          />
          <div className={['flex-row', 'gap-8'].join(' ')}>
            <Button
              id="copy-client-link-button"
              classNames={[styles.button]}
              text="Copy link"
              onClick={() => copyLinkHandler(clientUrl)}
            />
            <Button
              id="open-in-browser-button"
              classNames={[styles.button]}
              text="Open in browser"
              onClick={openInBrowserHandler}
            />
          </div>
        </div>
        <div className={[styles.row, 'vertically-centered', 'gap-16'].join(' ')}>
          <InfoItem
            classNames={['flex-1']}
            title="Link for member to join the video call"
            content={patientUrl}
            textId="video-call-link"
          />
          <div className={['flex-row', 'gap-8'].join(' ')}>
            <Button
              id="copy-member-link-button"
              classNames={[styles.button]}
              text="Copy link"
              onClick={() => copyLinkHandler(patientUrl)}
            />
            <Button
              id="send-sms-to-member-button"
              classNames={[styles.button]}
              text="Send SMS"
              onClick={sendSmsToMemberHandler}
              isLoading={isSmsLoading}
            />
            <Button
              id="send-email-to-member-button"
              classNames={[styles.button]}
              text="Send email"
              onClick={sendEmailToMemberHandler}
              isLoading={isEmailLoading}
            />
          </div>
        </div>
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
      })}
    </BasePopup>
  );
};

VideoCallInformationPopup.propTypes = {
  patientId: PropTypes.number,
  meetingId: PropTypes.string,
  patientUrl: PropTypes.string,
  clientUrl: PropTypes.string,
  onClose: PropTypes.func,
};

export default VideoCallInformationPopup;
